// PartialAccumulator: assistant/chunk accumulator.
// Folds the six StreamChunk variants into AssistantBlock[] keyed by block index;
// block-level immutability (a delta only swaps that block's reference).
import { toAssistantBlock } from "./conversation.js";
/**
 * Whether a stream chunk changes the partial assistant projection shown by the UI.
 * @param type - Stream chunk discriminant.
 * @returns Whether publishing the accumulated partial can change the visible snapshot.
 */
export function isVisibleAssistantChunk(type) {
    return type === 'block-start'
        || type === 'text-delta'
        || type === 'reasoning-delta'
        || type === 'tool-call-delta'
        || type === 'block-end';
}
/** assistant/chunk accumulator: folds StreamChunks into AssistantBlock[] with block-level immutability. */
export class PartialAccumulator {
    turn;
    step;
    // Sparse on purpose: block-start may arrive out of order, leaving holes until compaction.
    blocks = [];
    changed = true;
    snapshot;
    /**
     * @param turn - Owning agent turn.
     * @param step - Owning model step.
     * @param initialBlocks - Materialized prefix when accumulation begins after history replay.
     */
    constructor(turn, step, initialBlocks = []) {
        this.turn = turn;
        this.step = step;
        this.blocks = [...initialBlocks];
        this.snapshot = { turn, step, blocks: initialBlocks };
    }
    /**
     * Fold one chunk.
     * @param chunk - the stream chunk.
     * @returns whether it caused a visible change (usage/finish return false, skipping notification).
     */
    push(chunk) {
        switch (chunk.type) {
            case 'block-start': {
                this.blocks[chunk.index] = emptyAssistantBlock(chunk.blockType);
                this.changed = true;
                return true;
            }
            case 'text-delta': {
                const prev = this.blocks[chunk.index];
                this.blocks[chunk.index] = { kind: 'text', text: (prev?.kind === 'text' ? prev.text : '') + chunk.text };
                this.changed = true;
                return true;
            }
            case 'reasoning-delta': {
                const prev = this.blocks[chunk.index];
                this.blocks[chunk.index] = { kind: 'reasoning', text: (prev?.kind === 'reasoning' ? prev.text : '') + chunk.text };
                this.changed = true;
                return true;
            }
            case 'tool-call-delta': {
                const prev = this.blocks[chunk.index];
                const base = prev?.kind === 'tool-call' ? prev : { kind: 'tool-call', callId: '', name: '', argsRaw: '' };
                this.blocks[chunk.index] = {
                    kind: 'tool-call',
                    callId: base.callId || String(chunk.id),
                    name: chunk.name ?? base.name,
                    argsRaw: base.argsRaw + chunk.argumentsDelta,
                };
                this.changed = true;
                return true;
            }
            case 'block-end': {
                this.blocks[chunk.index] = toAssistantBlock(chunk.block);
                this.changed = true;
                return true;
            }
            default:
                // usage / finish / merge-extensible unknown variants: no visible block change
                // (finish is immediately followed by the assistant/message that supersedes the partial).
                return false;
        }
    }
    /**
     * Current partial projection.
     * @returns the cached snapshot (the blocks array reference only changes after a mutation).
     */
    toPartial() {
        if (this.changed) {
            // Compact sparse indexes (out-of-order block-start) into render order.
            this.snapshot = { turn: this.turn, step: this.step, blocks: this.blocks.filter((b) => b !== undefined) };
            this.changed = false;
        }
        return this.snapshot;
    }
}
/**
 * Create the empty client projection for one streamed Assistant block kind.
 * @param blockType - wire block kind.
 * @returns empty projected block ready to receive deltas.
 */
export function emptyAssistantBlock(blockType) {
    switch (blockType) {
        case 'text': return { kind: 'text', text: '' };
        case 'reasoning': return { kind: 'reasoning', text: '' };
        case 'tool-call': return { kind: 'tool-call', callId: '', name: '', argsRaw: '' };
        default: return { kind: 'other', block: null };
    }
}
//# sourceMappingURL=partial.js.map